import {Button, Dialog, IconButton} from '@wandb/components';
import * as FuzzyMatch from '@wandb/weave/common/util/fuzzyMatch';
import {isInvalidTag} from '@wandb/weave/common/util/tags';
import {Alias, getTagContrastColor, Tag} from '@wandb/weave/components/Tag';
import * as React from 'react';
import {Input} from 'semantic-ui-react';

import {type ApolloQueryResult} from '../state/graphql/apolloWithAbort';
import type * as GQLTypes from '../types/graphql';

export type SaveTags = (tags: string[]) => Promise<ApolloQueryResult<{}>>;

type PopoverPlacement = NonNullable<
  React.ComponentProps<typeof Dialog.Popover>['placement']
>;

interface TagAddButtonProps {
  tags: GQLTypes.Tag[];
  availableTags: GQLTypes.Tag[];
  compact?: boolean;
  placement?: PopoverPlacement;
  noun?: 'tag' | 'alias';
  iconOnly?: boolean;
  showColor?: boolean;
  addTag(tag: GQLTypes.Tag): Promise<void>;
  /** Return an error message to keep the popover open and block the add. */
  validateTag?: (tagName: string) => string | undefined;
}

export const TagAddButton: React.FC<TagAddButtonProps> = ({
  addTag,
  availableTags,
  compact,
  placement = 'left',
  noun,
  iconOnly,
  validateTag,
}) => {
  const [searchString, setSearchString] = React.useState('');
  const [isAdding, setAdding] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string>();
  let tagOptions = availableTags.toSorted();
  tagOptions = FuzzyMatch.fuzzyMatchWithMapping(
    tagOptions,
    searchString,
    t => t.name
  );
  noun = noun ?? 'tag';
  const nextColorIndex = availableTags.length;

  const onTagAdd = async (tag: GQLTypes.Tag, closer: () => void) => {
    const error = validateTag?.(tag.name);
    if (error != null) {
      setErrorMessage(error);
      return;
    }
    setAdding(true);
    await addTag(tag);
    closer();
    setAdding(false);
  };

  return (
    <Dialog.Trigger>
      {iconOnly || compact ? (
        <IconButton
          icon="add-new"
          variant="ghost"
          size="sm"
          aria-label={`Add a ${noun}`}
          data-test="tag-add-button"
        />
      ) : (
        <Button
          variant="ghost"
          size="sm"
          startIcon="tag">{`Add a ${noun}`}</Button>
      )}
      <Dialog.Popover placement={placement} aria-label={`Add a new ${noun}`}>
        {({close}) => (
          <div className="tag-add-button-menu">
            <Input
              onClick={(e: any) => e.stopPropagation()}
              icon="search"
              className="search tag-add-button-search"
              placeholder={`Search or create a new ${noun}`}
              value={searchString}
              loading={isAdding}
              error={errorMessage != null}
              onChange={(e, {value}) => {
                if (noun === 'tag' && isInvalidTag(value)) {
                  return;
                }
                setErrorMessage(undefined);
                setSearchString(value);
              }}
              onKeyPress={async (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' && searchString !== '') {
                  await onTagAdd(
                    {
                      name: searchString.trim(),
                      colorIndex: nextColorIndex,
                      icon: noun === 'tag' ? 'tag' : undefined,
                    },
                    close
                  );
                }
              }}
            />
            {errorMessage != null && (
              <div className="tag-add-button-error" data-test="tag-add-error">
                {errorMessage}
              </div>
            )}
            <div className="tag-add-button-results">
              {tagOptions.map((tag, index) => (
                <div
                  className="tag-add-button-result-item"
                  data-test="tag-result"
                  // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
                  key={index}
                  onClick={async () => {
                    await onTagAdd(tag, close);
                  }}>
                  {noun === 'tag' && (
                    <Tag
                      label={tag.name}
                      color={getTagContrastColor(tag.colorIndex)}
                    />
                  )}
                  {noun === 'alias' && (
                    <Alias
                      label={tag.name}
                      color={getTagContrastColor(tag.colorIndex)}
                    />
                  )}
                </div>
              ))}
              {searchString &&
                !availableTags.some(tag => {
                  return tag.name.toLowerCase() === searchString.toLowerCase();
                }) && (
                  <div
                    key="__create-new"
                    className="tag-add-button-result-item"
                    style={{display: 'flex'}}
                    data-test="create-new-tag"
                    onClick={async () => {
                      await onTagAdd(
                        {
                          name: searchString,
                          colorIndex: nextColorIndex,
                        },
                        close
                      );
                    }}>
                    <span style={{marginRight: '4px'}}>
                      Create new {noun}:{' '}
                    </span>
                    {/* Display the color of the tag that will appear in the cell */}
                    {noun === 'tag' && (
                      <Tag
                        label={searchString}
                        color={getTagContrastColor(nextColorIndex)}
                      />
                    )}
                    {noun === 'alias' && (
                      <Alias
                        label={searchString}
                        color={getTagContrastColor(nextColorIndex)}
                      />
                    )}
                  </div>
                )}
            </div>
          </div>
        )}
      </Dialog.Popover>
    </Dialog.Trigger>
  );
};
